/**
 * Histórico das solicitações do próprio funcionário, só de leitura.
 *
 * Papel: lista, da mais recente para a mais antiga, as solicitações enviadas pelo
 * usuário logado, com data e hora, número, descrição, fornecedor, quantidade,
 * valor unitário e miniatura quando há foto.
 *
 * Como usar: não recebe props; a lista vem de `useSolicitacoes`, que a API já
 * filtra pelo autor. A miniatura é um componente à parte que só é montado para
 * linhas com `temFoto`, e por isso só essas disparam a busca da imagem.
 */
import { formatarDinheiro, formatarInstante } from '@/shared/lib/formato';

import { useFotoDaSolicitacao, useSolicitacoes } from '../hooks/usar-solicitacoes';
import { type SolicitacaoDoHistorico } from '../types';

/**
 * Miniatura da foto de uma solicitação, com a imagem buscada ao ser montada.
 *
 * Quem chama é `LinhaDoHistorico`, e só para linhas com `temFoto`. Enquanto a foto
 * carrega, e quando ela não chega, a caixa mostra um texto curto no lugar.
 *
 * @param props.id - Identificador da solicitação cuja foto será lida.
 * @returns A caixa quadrada com a imagem, ou o texto de espera ou de ausência.
 */
function MiniaturaDaSolicitacao({ id }: { id: string }) {
  const consulta = useFotoDaSolicitacao(id);
  const foto = consulta.data?.foto ?? null;

  return (
    <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-linha-forte bg-muted">
      {foto !== null ? (
        <img src={foto} alt="Foto da solicitação" className="h-full w-full object-cover" />
      ) : (
        <span className="px-1 text-center text-[10px] leading-tight text-texto-fraco">
          {consulta.isPending ? 'Carregando' : 'Sem foto'}
        </span>
      )}
    </div>
  );
}

/**
 * Uma linha do histórico, com os dados da solicitação e a miniatura quando houver.
 *
 * @param props.linha - A solicitação como a listagem a devolve.
 * @returns O item da lista.
 */
function LinhaDoHistorico({ linha }: { linha: SolicitacaoDoHistorico }) {
  return (
    <li className="flex gap-4 rounded-md border border-linha-forte bg-superficie p-4">
      {linha.temFoto && <MiniaturaDaSolicitacao id={linha.id} />}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="font-mono text-xs text-texto-fraco">
          {formatarInstante(linha.criadoEm)} · {linha.numero}
        </p>
        <p className="font-medium break-words text-foreground">{linha.descricao}</p>
        <p className="text-sm break-words text-muted-foreground">{linha.fornecedor}</p>
        <p className="font-mono text-xs text-texto-fraco">
          Qtd. {linha.quantidade} · Valor unit. {formatarDinheiro(linha.valorUnitario)}
        </p>
      </div>
    </li>
  );
}

/**
 * Lista das solicitações enviadas pelo usuário logado, com os três estados da consulta.
 *
 * Enquanto a consulta carrega, mostra um aviso; se falha, mostra a mensagem de
 * falha no lugar da lista, e não uma lista vazia que sugeriria que nada foi
 * enviado. A lista vazia tem texto próprio.
 *
 * @returns O histórico, o aviso de espera, a falha ou o texto de lista vazia.
 */
export function HistoricoDeSolicitacoes() {
  const consulta = useSolicitacoes();

  if (consulta.isPending) {
    return <p className="text-sm text-muted-foreground">Carregando seu histórico…</p>;
  }

  if (consulta.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Não foi possível carregar seu histórico.
      </p>
    );
  }

  if (consulta.data.length === 0) {
    return <p className="text-sm text-muted-foreground">Nenhum pedido enviado ainda.</p>;
  }

  return (
    <ul className="flex flex-col gap-3">
      {consulta.data.map((linha) => (
        <LinhaDoHistorico key={linha.id} linha={linha} />
      ))}
    </ul>
  );
}
